import { quoteSale } from '../content/market';
import type { SwitchMapConfig, SwitchMapMachineEntity } from '../core/entities';
import type { SimEvent } from '../core/events';
import {
  peekPort,
  portHasRoom,
  singleInput,
  singleOutput,
  tryPushToPort,
  tryTakeFromPort,
} from '../core/port';
import { errorSubscription, thrownMessage } from '../core/subscription';
import type { SimState } from '../core/state';
import { drainOutput } from './shared';
import type { MachineHandler } from './registry';

/**
 * An inner subscription cannot outlive the outer one, so a teardown abandons whatever was in flight.
 * Keyed on `state.subscribed` alone rather than on tracing a specific observable upstream: this
 * operator never has to name one, and a trace that fails to walk back through an intervening machine
 * would read as an unsubscribe and quietly cancel every sale.
 */
function dropPendingWhileUnsubscribed(
  state: SimState,
  machine: SwitchMapMachineEntity,
  events: SimEvent[],
): void {
  if (!state.subscribed) cancelPending(state, machine, events);
}

function cancelPending(state: SimState, machine: SwitchMapMachineEntity, events: SimEvent[]): void {
  const pending = machine.internal.pending;
  if (!pending) return;
  machine.internal.pending = null;
  delete state.packets[pending.packetId];
  events.push({ type: 'saleCancelled', packetId: pending.packetId, amount: pending.amount });
  events.push({ type: 'packetDespawned', packetId: pending.packetId });
}

/**
 * Settles ahead of the tick's intake, so the settle delay reads as a deadline: packets arriving
 * strictly faster than it are what cost you money. The production credit is booked even when the
 * packet record has since vanished, because the sale did happen — it is the proceeds that were lost.
 */
function settleDueSale(state: SimState, machine: SwitchMapMachineEntity, events: SimEvent[]): void {
  const pending = machine.internal.pending;
  if (!pending) return;

  pending.ticksRemaining -= 1;
  const outputPort = singleOutput(machine.outputs);
  if (pending.ticksRemaining > 0 || !portHasRoom(outputPort)) return;

  machine.internal.pending = null;
  state.economy.materialSaleCounts[pending.material] =
    (state.economy.materialSaleCounts[pending.material] ?? 0) + 1;

  const settled = state.packets[pending.packetId];
  if (!settled) return;
  settled.material = { cash: pending.amount };
  events.push({
    type: 'packetTransformed',
    packetId: pending.packetId,
    material: settled.material,
  });
  tryPushToPort(outputPort, pending.packetId);
}

/**
 * Calls the market with the sell method the player installed — and because the market answers with a
 * sale rather than a price, has to decide what a packet arriving mid-sale means. `switchMap`'s answer
 * is to cancel: the in-flight sale is abandoned and its proceeds never arrive.
 *
 * An unsellable packet throws here rather than at the Subscriber, which is the only reason a
 * `catchError` upstream could ever see one.
 */
function startSaleForWaitingPacket(
  state: SimState,
  machine: SwitchMapMachineEntity,
  config: SwitchMapConfig,
  events: SimEvent[],
): void {
  const inputPort = singleInput(machine.inputs);
  const waitingId = peekPort(inputPort);
  if (waitingId === undefined) return;

  const packet = state.packets[waitingId];
  if (!packet) {
    tryTakeFromPort(inputPort);
    return;
  }

  try {
    const quote = quoteSale(config.method, packet.material);
    tryTakeFromPort(inputPort);
    cancelPending(state, machine, events);
    machine.internal.pending = {
      packetId: waitingId,
      material: quote.material,
      amount: quote.amount,
      ticksRemaining: quote.settleTicks,
    };
  } catch (error) {
    tryTakeFromPort(inputPort);
    events.push(
      ...errorSubscription(state, {
        packetId: waitingId,
        material: packet.material,
        message: thrownMessage(error),
      }),
    );
  }
}

/**
 * The only operator here that can destroy value, and only under load. Feed it slower than a sale
 * takes to settle and it behaves exactly like `concatMap` or `mergeMap` would, which is the point —
 * the three differ solely in what they do about overlap.
 */
export const switchMapHandler: MachineHandler = {
  kind: 'switchMap',
  step({ state, machine }) {
    if (machine.kind !== 'switchMap') return [];
    const events: SimEvent[] = [];

    dropPendingWhileUnsubscribed(state, machine, events);
    drainOutput(state, machine, events);
    if (!machine.config) return events;

    settleDueSale(state, machine, events);
    startSaleForWaitingPacket(state, machine, machine.config, events);
    return events;
  },
};
